import React from "react";
import { motion } from "framer-motion";
import { ABSTRACT_BG } from "../data/content";
import { useContent, resolveImg } from "../context/ContentContext";

const Hero = () => {
  const { content } = useContent();
  return (
    <section
      id="top"
      className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden"
      data-testid="hero-section"
    >
      <div className="pointer-events-none absolute inset-0 -z-10">
        <img src={ABSTRACT_BG} alt="" aria-hidden className="absolute -top-40 -right-40 w-[70vw] max-w-[900px] opacity-40 animate-drift-slow" />
        <img src={ABSTRACT_BG} alt="" aria-hidden className="absolute -bottom-60 -left-40 w-[60vw] max-w-[800px] opacity-25 animate-drift-slower rotate-180" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.32em] text-zinc-500"
          data-testid="hero-eyebrow"
        >
          <span className="h-px w-10 bg-black/40" />
          {content.hero_eyebrow}
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 32 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
          className="font-display mt-6 md:mt-8 text-[15vw] leading-[0.88] md:text-[10rem] lg:text-[12rem] font-light tracking-[-0.04em] text-black"
          data-testid="hero-title"
        >
          {content.hero_title_line1}
          <span className="block font-semibold">{content.hero_title_line2}</span>
        </motion.h1>

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-7 text-zinc-600 text-lg md:text-2xl leading-relaxed max-w-2xl"
            data-testid="hero-tagline"
          >
            {content.hero_tagline}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="md:col-span-5 flex items-center gap-4 md:justify-end"
          >
            <a href="#contact" onClick={(e) => { e.preventDefault(); document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }); }}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-black text-white text-sm font-medium hover:bg-zinc-800 transition-colors"
              data-testid="hero-primary-cta">
              Get a quote <span aria-hidden>→</span>
            </a>
            <a href="#about" onClick={(e) => { e.preventDefault(); document.getElementById("about")?.scrollIntoView({ behavior: "smooth" }); }}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-black text-sm font-medium border border-black/15 hover:border-black transition-colors"
              data-testid="hero-secondary-cta">
              Our story
            </a>
          </motion.div>
        </div>

        <motion.figure
          initial={{ opacity: 0, y: 60, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1.4, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative mt-16 md:mt-24 rounded-[2rem] md:rounded-[2.5rem] overflow-hidden shadow-[0_30px_80px_-30px_rgba(0,0,0,0.35)]"
          data-testid="hero-team-photo"
        >
          <div className="relative aspect-[16/10] md:aspect-[21/9] bg-zinc-100">
            {content.hero_image && (content.hero_image.endsWith('.mp4') || content.hero_image.endsWith('.webm') || content.hero_image.endsWith('.mov')) ? (
              <video src={resolveImg(content.hero_image)} autoPlay muted loop playsInline className={`absolute inset-0 w-full h-full object-cover ${content.hero_grayscale ? "grayscale" : ""}`} />
            ) : (
              <img src={resolveImg(content.hero_image)} alt="The BELIEVE TEAM" className={`absolute inset-0 w-full h-full object-cover ${content.hero_grayscale ? "grayscale" : ""}`} loading="eager" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            <div className="absolute bottom-6 md:bottom-10 left-6 md:left-12 right-6 md:right-12 flex items-end justify-between text-white">
              <div>
                <div className="text-[10px] md:text-xs font-medium uppercase tracking-[0.28em] opacity-80">
                  {content.hero_photo_caption_label}
                </div>
                <div className="font-display text-xl md:text-3xl mt-2">
                  {content.hero_photo_caption_title}
                </div>
              </div>
            </div>
          </div>
        </motion.figure>

        <div className="mt-16 md:mt-20 border-y border-black/10 py-6 overflow-hidden" data-testid="press-strip">
          <div className="flex items-center gap-16 whitespace-nowrap animate-marquee w-max">
            {[...(content.press || []), ...(content.press || [])].map((p, i) => (
              <span key={i} className="font-display text-sm md:text-base tracking-[0.32em] text-zinc-500">{p}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
